import { useEffect, useState } from 'react'
import { Alert } from '../atoms/Alert'
import { Button } from '../atoms/Button'
import { Modal } from '../atoms/Modal'
import { Textarea } from '../atoms/Textarea'

const REASON_MIN = 10
const REASON_MAX = 2000

interface Props {
  readonly open: boolean
  readonly orderNumber: string
  readonly onClose: () => void
  readonly onSubmit: (reason: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

/**
 * Shared "Request return" modal — used on both the customer order
 * list (`/account/orders`) and the order detail (`/account/orders/:id`)
 * pages. Kiran 2026-06-18.
 *
 * Validates the reason length client-side (10–2000), shows the live
 * char count, and surfaces the server message on failure.
 */
export function RequestReturnModal({ open, orderNumber, onClose, onSubmit }: Props): JSX.Element {
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setReason('')
      setSubmitting(false)
      setError(null)
    }
  }, [open])

  const handleSubmit = async (): Promise<void> => {
    setError(null)
    const trimmed = reason.trim()
    if (trimmed.length < REASON_MIN) {
      setError(`Reason must be at least ${REASON_MIN} characters`)
      return
    }
    if (trimmed.length > REASON_MAX) {
      setError(`Reason exceeds ${REASON_MAX} characters`)
      return
    }
    setSubmitting(true)
    const res = await onSubmit(trimmed)
    setSubmitting(false)
    if (res.ok) {
      onClose()
    } else {
      setError(res.message)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Request a return"
      maxWidth="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit return'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error !== null ? <Alert tone="error">{error}</Alert> : null}
        <p className="font-body text-body-sm text-text-secondary">
          Tell us why you&apos;d like to return order{' '}
          <span className="font-mono">{orderNumber}</span>. Our team will review and
          respond.
        </p>
        <Textarea
          value={reason}
          rows={5}
          maxLength={REASON_MAX}
          onChange={(e) => setReason(e.target.value)}
          placeholder={`Describe the issue or reason for return (min ${REASON_MIN} characters)`}
        />
        <p className="font-body text-body-xs text-text-secondary text-right">
          {reason.trim().length} / {REASON_MAX}
        </p>
      </div>
    </Modal>
  )
}
